import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMergeRefs } from '@floating-ui/react';
import type { CSSProperties, ReactElement, Ref } from 'react';
import { cloneElement } from 'react';

interface BaseItem {
  id: UniqueIdentifier;
}

interface Props<T extends BaseItem> {
  item: T;
  renderItem: (item: T) => ReactElement;
  isDisabled?: boolean;
  handle?: boolean;
}

const SortableItem = <T extends BaseItem>(props: Props<T>) => {
  const { item, renderItem, isDisabled, handle = false } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: isDisabled });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0 : undefined,
  };

  const child = renderItem(item);
  const childProps = child.props as {
    style?: CSSProperties;
    ref?: Ref<unknown>;
  };
  const mergedRef = useMergeRefs([setNodeRef, childProps.ref]);

  return cloneElement(
    child,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {
      ref: mergedRef,
      style: { ...childProps.style, ...style },
      role: 'listitem',
      // In non-handle mode the whole element is the draggable, so dnd-kit's
      // attributes (incl. its own `role`) take precedence over `listitem`.
      ...(handle ? {} : { ...attributes, ...listeners }),
      ...(isDragging ? { 'data-dragging': true } : {}),
    } as any
  );
};

export default SortableItem;
